import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { MemberGrowthLocaleStorageSearchFilter, MemberGrowthSearchFilter } from 'src/modules/statistics/models/memberGrowthSearchFilter';
import {
  PaymentDistributionLocalStorageSearchFilter,
  PaymentDistributionSearchFilter,
} from 'src/modules/statistics/models/paymentDistributionSearchFilter';
import dayjs from 'dayjs';
import { useGlobals } from './appContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { LocalStorageKeys } from '../models/enums/localStorageKeys.enum';
import { Association } from '../models/association/association';
import { District } from '../models/district/district';
import { DateHelper } from '../helpers/dateHelper';

export interface StatisticsContextSettings {
  district: District | undefined;
  setDistrict: React.Dispatch<React.SetStateAction<District | undefined>>;
  association: Association | undefined;
  setAssociation: React.Dispatch<React.SetStateAction<Association | undefined>>;
  memberGrowthSearchFilter: MemberGrowthLocaleStorageSearchFilter;
  setMemberGrowthSearchFilter: React.Dispatch<React.SetStateAction<MemberGrowthLocaleStorageSearchFilter>>;
  searchFilter: MemberGrowthSearchFilter;
  paymentDistStorageSearchFilter: PaymentDistributionLocalStorageSearchFilter;
  setPaymentDistStorageSearchFilter: React.Dispatch<React.SetStateAction<PaymentDistributionLocalStorageSearchFilter>>;
  paymentDistSearchFilter: PaymentDistributionSearchFilter;
}

export const StatisticsContext = createContext<StatisticsContextSettings | undefined>(undefined);

export const useStatisticsContext = () => {
  const context = useContext(StatisticsContext);
  if (context === undefined) {
    throw new Error('useStatisticsContext must be within StatisticsContextProvider');
  }
  return context;
};

interface Props {
  children: JSX.Element;
}

export const StatisticsContextProvider = ({ children }: Props) => {
  const { currentUser } = useGlobals();

  // MemberGrowth
  const [district, setDistrict] = useState<District | undefined>(undefined);
  const [association, setAssociation] = useState<Association | undefined>(undefined);

  // Auto-förvälj om användaren bara hör till exakt EN förening (utan distrikt) eller
  // exakt ETT distrikt (utan föreningar). Kör bara om inget är valt — så ett tidigare
  // localStorage-restorerat val (i useEffect:en nedan) eller manuellt val inte överskrivs.
  useEffect(() => {
    if (!currentUser || association || district) return;
    if (currentUser.districts.length === 0 && currentUser.associations.length === 1) {
      setAssociation(currentUser.associations[0] as unknown as Association);
    } else if (currentUser.associations.length === 0 && currentUser.districts.length === 1) {
      setDistrict(currentUser.districts[0] as unknown as District);
    }
  }, [currentUser, association, district]);

  const [memberGrowthSearchFilter, setMemberGrowthSearchFilter] = useLocalStorage(LocalStorageKeys.StatisticsMemberGrowthSearchFilter, {
    fromSnapshotTime: undefined,
    toSnapshotTime: undefined,
    district,
    association,
  } as MemberGrowthLocaleStorageSearchFilter);

  const searchFilter: MemberGrowthSearchFilter = useMemo(() => {
    return {
      fromSnapshotTimeId: memberGrowthSearchFilter.fromSnapshotTime?.snapshotTime_id ?? 0,
      toSnapshotTimeId: memberGrowthSearchFilter.toSnapshotTime?.snapshotTime_id ?? 0,
      districtId: memberGrowthSearchFilter.district?.countyAss_id,
      associationId: memberGrowthSearchFilter.association?.localAss_id,
    } as MemberGrowthSearchFilter;
  }, [
    memberGrowthSearchFilter.association?.localAss_id,
    memberGrowthSearchFilter.district?.countyAss_id,
    memberGrowthSearchFilter.fromSnapshotTime?.snapshotTime_id,
    memberGrowthSearchFilter.toSnapshotTime?.snapshotTime_id,
  ]);

  useEffect(() => {
    if (!memberGrowthSearchFilter.association || !memberGrowthSearchFilter.district) {
      setMemberGrowthSearchFilter((prev) => ({ ...prev, association, district }));
    }
  }, [setMemberGrowthSearchFilter, memberGrowthSearchFilter.association, memberGrowthSearchFilter.district, association, district]);

  useEffect(() => {
    if (memberGrowthSearchFilter.association) {
      setAssociation(memberGrowthSearchFilter.association);
    }
    if (memberGrowthSearchFilter.district) {
      setDistrict(memberGrowthSearchFilter.district);
    }
  }, [memberGrowthSearchFilter.association, memberGrowthSearchFilter.district]);

  useEffect(() => {
    if (memberGrowthSearchFilter.association && memberGrowthSearchFilter.district) {
      setMemberGrowthSearchFilter((prev) => ({ ...prev, association, district }));
    }
  }, [association, district, memberGrowthSearchFilter.association, memberGrowthSearchFilter.district, setMemberGrowthSearchFilter]);

  // PaymentDistribution
  const [paymentDistStorageSearchFilter, setPaymentDistStorageSearchFilter] = useLocalStorage(
    LocalStorageKeys.StatisticsPaymentDistributionSearchFilter,
    {
      start: dayjs().startOf('month').toDate(),
      end: new Date(),
    } as PaymentDistributionLocalStorageSearchFilter,
  );

  const paymentDistSearchFilter: PaymentDistributionSearchFilter = useMemo(() => {
    return {
      start: DateHelper.postDateTime(paymentDistStorageSearchFilter.start),
      end: DateHelper.postDateTime(paymentDistStorageSearchFilter.end),
    } as PaymentDistributionSearchFilter;
  }, [paymentDistStorageSearchFilter.end, paymentDistStorageSearchFilter.start]);

  const statisticsContext = useMemo(
    () => ({
      district,
      setDistrict,
      association,
      setAssociation,
      memberGrowthSearchFilter,
      setMemberGrowthSearchFilter,
      searchFilter,
      paymentDistStorageSearchFilter,
      setPaymentDistStorageSearchFilter,
      paymentDistSearchFilter,
    }),
    [
      association,
      district,
      memberGrowthSearchFilter,
      paymentDistSearchFilter,
      paymentDistStorageSearchFilter,
      searchFilter,
      setMemberGrowthSearchFilter,
      setPaymentDistStorageSearchFilter,
    ],
  );

  return <StatisticsContext.Provider value={statisticsContext}>{children}</StatisticsContext.Provider>;
};
